import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CampaignMessagingProps,
  GetCampaignMessagingResponse,
  GetCampaignResponse,
  CampaignErrorResponse,
} from '@dreamai/types';
import api from '../../../../api';
import { useDependencyUpdates } from '../../../../providers';

import { ReactComponent as ReplyIcon } from '../../../../icons/flip-back.svg';
import { ReactComponent as SignatureIcon } from '../../../../icons/signature.svg';
import { ReactComponent as EmailXIcon } from '../../../../icons/email-x.svg';
import {
  calculateDeliverabilityScore,
  hasDeliverabilityIssues,
  DeliverabilityScore,
} from '../../../../utils/deliverability';

import CampaignEditMessagingEmail from './CampaignEditMessagingEmail';
import SignatureView from './SignatureView';
import ErrorState from '../../../common/ErrorState';
import { sanitizedHtml } from '../../../../utils/sanitize';
import './campaign-messaging.scss';

interface CampaignMessagingComponentProps extends CampaignMessagingProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  isInboxView?: boolean;
  onInboxViewComponentViewClick?: (componentId: string) => void;
}

const CampaignMessaging: React.FC<CampaignMessagingComponentProps> = ({
  campaignId,
  sequenceIds,
  threadId,
  updateThreadViewVisualDescription,
  isInboxView,
  onInboxViewComponentViewClick,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState<string>('');
  const [isLive, setIsLive] = useState<boolean>(false);
  const [sequences, setSequences] = useState<
    GetCampaignMessagingResponse['sequences']
  >([]);
  const [selectedSequenceId, setSelectedSequenceId] = useState<number | null>(
    null,
  );
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<
    'active' | 'draft' | 'inactive'
  >('active');
  const [editingEmailId, setEditingEmailId] = useState<number | null>(null);
  const [profileIds, setProfileIds] = useState<number[]>([]);
  const [showSignatureView, setShowSignatureView] = useState<boolean>(false);
  const [anyMissingSignature, setAnyMissingSignature] =
    useState<boolean>(false);
  const [profilesError, setProfilesError] = useState<string | null>(null);
  const [deliverabilityScore, setDeliverabilityScore] =
    useState<DeliverabilityScore | null>(null);

  const fetchMessaging = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<
        GetCampaignMessagingResponse | CampaignErrorResponse
      >(`/campaigns/${campaignId}/messaging`);

      if (response.data.success) {
        const data = response.data as GetCampaignMessagingResponse;
        setCampaignName(data.campaignName);
        setIsLive(data.isLive);

        // Filter sequences if sequenceIds prop is provided (for approval flow)
        const filteredSequences =
          sequenceIds && sequenceIds.length > 0
            ? data.sequences.filter((seq) => sequenceIds.includes(seq.id))
            : data.sequences;

        setSequences(filteredSequences);
      } else {
        setError(response.data.error);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch messaging',
      );
    } finally {
      setLoading(false);
    }
  }, [campaignId, sequenceIds, getAccessTokenSilently]);

  useEffect(() => {
    if (campaignId) {
      fetchMessaging();
    }
  }, [campaignId, fetchMessaging]);

  // Fetch deliverability data to show warning for ok/bad scores
  useEffect(() => {
    const fetchDeliverability = async () => {
      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get<
          GetCampaignResponse | CampaignErrorResponse
        >(`/campaigns/${campaignId}`);

        if (response.data.success) {
          const data = response.data as GetCampaignResponse;
          const deliverability = data.campaign.dash_data?.deliverability;

          if (
            deliverability?.optedIn &&
            deliverability?.hasEnoughData &&
            deliverability?.placements
          ) {
            const { score } = calculateDeliverabilityScore(
              deliverability.placements,
              deliverability.totalTests,
            );
            setDeliverabilityScore(score);
          }
        }
      } catch (err) {
        // Silently fail - deliverability warning is optional
        console.error('Failed to fetch deliverability:', err);
      }
    };

    if (campaignId) {
      fetchDeliverability();
    }
  }, [campaignId, getAccessTokenSilently]);

  // Sync selectedSequenceId with sequences and status filter
  useEffect(() => {
    if (sequences.length > 0) {
      // Get filtered sequences based on current status (using same logic as helper functions)
      const getFilteredSeqs = () => {
        if (selectedStatusFilter === 'active') {
          return sequences.filter((s) => s.active === true && s.draft !== true);
        } else if (selectedStatusFilter === 'draft') {
          return sequences.filter((s) => s.draft === true);
        } else {
          return sequences.filter(
            (s) => s.active === false && s.draft !== true,
          );
        }
      };

      const filtered = getFilteredSeqs();

      // If current selection is not in filtered list, select first from filtered
      const selectedInFiltered = filtered.some(
        (s) => s.id === selectedSequenceId,
      );
      if (!selectedSequenceId || !selectedInFiltered) {
        if (filtered.length > 0) {
          setSelectedSequenceId(filtered[0].id);
        } else {
          // No sequences in this filter, try to switch to a filter that has sequences
          if (
            selectedStatusFilter !== 'active' &&
            sequences.some((s) => s.active === true && s.draft !== true)
          ) {
            setSelectedStatusFilter('active');
          } else if (
            selectedStatusFilter !== 'draft' &&
            sequences.some((s) => s.draft === true)
          ) {
            setSelectedStatusFilter('draft');
          } else if (
            selectedStatusFilter !== 'inactive' &&
            sequences.some((s) => s.active === false && s.draft !== true)
          ) {
            setSelectedStatusFilter('inactive');
          }
        }
      }
    } else if (selectedSequenceId !== null) {
      // No sequences available, clear selection
      setSelectedSequenceId(null);
    }
  }, [sequences, selectedSequenceId, selectedStatusFilter]);

  // Fetch campaign profiles
  const fetchCampaignProfiles = useCallback(async () => {
    try {
      setProfilesError(null);
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<{
        success: boolean;
        profiles: Array<{ id: number; hasSignature: boolean }>;
        anyMissingSignature: boolean;
        error?: string;
      }>(`/signatures/campaign/${campaignId}/profiles`);

      if (response.data.success && response.data.profiles) {
        setProfileIds(response.data.profiles.map((p) => p.id));
        setAnyMissingSignature(response.data.anyMissingSignature);
      } else if (response.data.error) {
        setProfilesError(response.data.error);
      }
    } catch (err) {
      console.error('Error fetching campaign profiles:', err);
      setProfilesError(
        err instanceof Error ? err.message : 'Failed to load campaign profiles',
      );
    }
  }, [campaignId, getAccessTokenSilently]);

  useEffect(() => {
    if (campaignId) {
      fetchCampaignProfiles();
    }
  }, [campaignId, fetchCampaignProfiles]);

  // Listen for campaign updates
  const { registerCallback } = useDependencyUpdates();
  useEffect(() => {
    const unregister = registerCallback('campaigns', campaignId, () => {
      console.log('Campaign updated in messaging, reloading...');
      fetchMessaging();
    });

    return () => unregister();
  }, [campaignId, registerCallback, fetchMessaging]);

  if (loading) {
    return <div>Loading messaging...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  // Helper functions to categorize sequences
  const isActiveSequence = (
    seq: GetCampaignMessagingResponse['sequences'][0],
  ) => seq.active === true && seq.draft !== true;
  const isDraftSequence = (seq: GetCampaignMessagingResponse['sequences'][0]) =>
    seq.draft === true;
  const isInactiveSequence = (
    seq: GetCampaignMessagingResponse['sequences'][0],
  ) => seq.active === false && seq.draft !== true;

  // Calculate counts for each status
  const activeSequences = sequences.filter(isActiveSequence);
  const draftSequences = sequences.filter(isDraftSequence);
  const inactiveSequences = sequences.filter(isInactiveSequence);

  // Filter sequences based on selected status
  const filteredSequences =
    selectedStatusFilter === 'active'
      ? activeSequences
      : selectedStatusFilter === 'draft'
        ? draftSequences
        : inactiveSequences;

  // Get the currently selected sequence
  const sequence = sequences.find((s) => s.id === selectedSequenceId);

  if (sequences.length === 0) {
    return (
      <div className='campaign-messaging'>
        <p>{campaignName}</p>
        <h2>Messaging</h2>
        <p>No email sequence found for this campaign.</p>
      </div>
    );
  }

  if (!sequence) {
    return (
      <div className='campaign-messaging'>
        <p>{campaignName}</p>
        <h2>Messaging</h2>
        <p>Loading sequence...</p>
      </div>
    );
  }

  // Calculate cumulative days for each email
  const getEmailDay = (index: number) => {
    if (index === 0) return 0;
    let totalDays = 0;
    for (let i = 0; i <= index; i++) {
      totalDays += sequence.emails[i].delay;
    }
    return totalDays;
  };

  // Render email body based on content type
  const renderEmailBody = (email: (typeof sequence.emails)[0]) => {
    if (email.contentType === 'template') {
      const template = email.template || '';

      // Check if template already contains HTML tags (from WYSIWYG editor)
      const containsHtmlTags = /<\/?[a-z][\s\S]*>/i.test(template);

      if (containsHtmlTags) {
        // Template already has HTML tags, render directly without additional wrapping
        return <div className='body' {...sanitizedHtml(template)} />;
      } else {
        // Plain text template with \n characters - convert to HTML with inline styles
        // to match inbox rendering (12px bottom margin between paragraphs)
        const paragraphs = template.split('\n\n').filter((p) => p.trim());
        const htmlWithStyles = paragraphs
          .map((p) => {
            const withBreaks = p.replace(/\n/g, '<br />');
            return `<p style="margin: 0 0 12px 0; padding: 0;">${withBreaks}</p>`;
          })
          .join('');

        return <div className='body' {...sanitizedHtml(htmlWithStyles)} />;
      }
    } else {
      // Plain text with newlines - convert \n to <br />
      return (
        <div className='body'>
          {email.contents.split('\n').map((line, i) => (
            <span key={i}>
              {line}
              {i < email.contents.split('\n').length - 1 && <br />}
            </span>
          ))}
        </div>
      );
    }
  };

  // If editing an email, show the edit component
  const editingEmail = editingEmailId
    ? sequence.emails.find((e) => e.id === editingEmailId)
    : null;

  if (editingEmail) {
    // Check if this is the first email in the sequence (emailOrder === 0)
    const isFirstEmail = editingEmail.emailOrder === 0;

    return (
      <div className='campaign-messaging'>
        <h2>Messaging</h2>
        <CampaignEditMessagingEmail
          email={editingEmail}
          campaignId={campaignId}
          isFirstEmail={isFirstEmail}
          onCancel={() => setEditingEmailId(null)}
          onSave={() => {
            setEditingEmailId(null);
          }}
        />
      </div>
    );
  }

  return (
    <>
      {!showSignatureView && (
        <div className='campaign-messaging'>
          <h2>Messaging</h2>
          {profilesError && (
            <ErrorState
              title='Could not load campaign profiles'
              subtitle={profilesError}
            />
          )}

          <div className='additional-info-area'>
            {/* Status filter tabs - only show when there's more than one sequence */}
            {sequences.length > 1 && (
              <div className='sequence-status-tabs'>
                <div
                  className={`status-tab ${selectedStatusFilter === 'active' ? 'active' : ''}`}
                  onClick={() => setSelectedStatusFilter('active')}
                >
                  Active <span>{activeSequences.length}</span>
                </div>
                {draftSequences.length > 0 && (
                  <div
                    className={`status-tab ${selectedStatusFilter === 'draft' ? 'active' : ''}`}
                    onClick={() => setSelectedStatusFilter('draft')}
                  >
                    Draft <span>{draftSequences.length}</span>
                  </div>
                )}
                {inactiveSequences.length > 0 && (
                  <div
                    className={`status-tab ${selectedStatusFilter === 'inactive' ? 'active' : ''}`}
                    onClick={() => setSelectedStatusFilter('inactive')}
                  >
                    Inactive <span>{inactiveSequences.length}</span>
                  </div>
                )}
              </div>
            )}

            {/* Sequence dropdown (only show if there are multiple sequences in the filtered list) */}
            {filteredSequences.length > 1 && (
              <div className='sequence-dropdown'>
                <select
                  value={selectedSequenceId || ''}
                  onChange={(e) =>
                    setSelectedSequenceId(Number(e.target.value))
                  }
                >
                  {filteredSequences.map((seq) => (
                    <option key={seq.id} value={seq.id}>
                      {seq.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!isLive && sequences.length === 1 ? (
              <div className='draft-indicator'>
                <h3>
                  This is the set of emails that we'll send to your prospects
                </h3>
              </div>
            ) : selectedStatusFilter === 'inactive' ? (
              <div className='draft-indicator'>
                <h3>
                  These are historic or unused sequences that aren't being sent
                  to prospects
                </h3>
              </div>
            ) : (
              sequence?.draft && (
                <div className='draft-indicator'>
                  <h3>
                    This sequence is in draft. Press confirm or ask your Dreamie
                    to make it active.
                  </h3>
                </div>
              )
            )}

            {/* Deliverability warning for live sequences with ok/bad scores */}
            {!sequence?.draft &&
              sequence?.active &&
              deliverabilityScore &&
              hasDeliverabilityIssues(deliverabilityScore) && (
                <div className='deliverability-warning'>
                  <div className='warning-icon'>
                    <EmailXIcon />
                  </div>
                  <div className='warning-content'>
                    <div className='warning-title'>
                      This sequence may have deliverability issues
                    </div>
                    <div className='warning-description'>
                      The way your email is written might mean it doesn't appear
                      in people's inboxes. Ask Your Dreamie to fix this.
                    </div>
                  </div>
                </div>
              )}
          </div>

          <div className='messages-wrapper'>
            {sequence.emails.length === 0 ? (
              <div className='empty-state'>
                <div className='empty-state-content'>
                  <h3>No emails in this sequence yet</h3>
                  <p>
                    You can ask your Sales Pro to add emails to this campaign's
                    sequence.
                  </p>
                </div>
              </div>
            ) : (
              sequence.emails.map((email, index) => {
                const day = getEmailDay(index);
                return (
                  <div key={email.id} className='message-wrapper'>
                    <div className='day-spacer'>Day {day}</div>
                    <div className='message-content'>
                      <div className='header'>
                        <span>
                          {email.isNewThread ? (
                            email.subjectValue || 'No subject'
                          ) : (
                            <span className='reply-to-thread'>
                              <ReplyIcon /> Follow up to previous email
                            </span>
                          )}
                        </span>
                        <button
                          className='edit-button'
                          onClick={() => setEditingEmailId(email.id)}
                        >
                          Edit
                        </button>
                      </div>
                      {renderEmailBody(email)}
                      {email.isNewThread && !showSignatureView && (
                        <div className='signature-view-flipper'>
                          <SignatureIcon />
                          <span>
                            The sender's email signature will be added here
                          </span>
                          <button onClick={() => setShowSignatureView(true)}>
                            Manage
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {showSignatureView && (
        <div className='signature-view-container'>
          <div className='signature-view-header'>
            <button onClick={() => setShowSignatureView(false)}>
              ← Back to Messaging
            </button>
          </div>
          <SignatureView profileIds={profileIds} />
        </div>
      )}
    </>
  );
};

export default CampaignMessaging;
